import { Dispatch } from "@reduxjs/toolkit";
import { projectRoleRequestsApi } from "@src/services/apis/projectRoleRequestsApi";
import { projectsApi } from "@src/services/apis/projectsApi";
import { RoleRequest } from "@src/types/role_request_types";

/**
 * Updates the cached role request in the Redux store for the specified project.
 *
 * This is necessary because the AG Grid only reflects local UI changes, and the
 * Redux cache is not automatically updated unless revalidated. Manually updating
 * the cache improves performance by avoiding unnecessary re-fetching, especially
 * for projects with a large number of requests.
 *
 * @param dispatch - Redux dispatch function to trigger cache update.
 * @param projectId - The ID of the project whose cache should be updated.
 * @param updatedRequest - The updated RoleRequest object to patch into the cache.
 */
export function updateCachedRoleRequestsByProjectId(
  dispatch: Dispatch<any>,
  projectId: string,
  updatedRequest: RoleRequest
): void {
  dispatch(
    projectsApi.util.updateQueryData(
      "getRoleRequestsByProjectId",
      projectId,
      (roleRequests: RoleRequest[] = []) => {
        const index = roleRequests.findIndex(
          (req) => req.roleRequestId === updatedRequest.roleRequestId
        );
        if (index !== -1) {
          roleRequests[index] = updatedRequest;
        }
      }
    )
  );
}

/**
 * Update the cached role request in the Redux store for the request.
 *
 * This is necessary because when the user changes the additional information in the modal of the team lead,
 * then the data should be automatically reflected in the main modal without refetching and rerendering the modal.
 *
 * @param dispatch - Redux dispatch function to trigger cache update.
 * @param roleRequestId - The ID of the role request to refetch.
 * @param updatedRequest - The updated RoleRequest object to patch into the cache.
 */
export function updateCachedRoleRequestByRequestId(
  dispatch: Dispatch<any>,
  roleRequestId: number,
  updatedRequest: RoleRequest
): void {
  dispatch(
    projectRoleRequestsApi.util.updateQueryData(
      "getRoleRequestById",
      roleRequestId,
      (cachedRoleRequest: RoleRequest) => {
        Object.assign(cachedRoleRequest, updatedRequest);
      }
    )
  );
}
